/**
 * The panel-permission catalogue — the checklist a staff role grants from.
 *
 * The catalogue itself now lives in @platform/shared-types as ADMIN_SECTIONS
 * (shared with staff-service and admin-access). `PANEL_PERMISSION_GROUPS`
 * below is a re-export, grouped, kept ONLY for StaffRolesPage.tsx's interim
 * path-list editor until Task 12 replaces that page with a levels-aware one.
 *
 * Rules (mirrored by staff-service's hasPermission): '*' grants everything;
 * a granted parent path covers its children ('/inventory/stock' covers
 * '/inventory/stock/new'); '/staff' grants staff management itself
 * (hire/fire, workflows, verification, roles).
 *
 * '/staff-portal' and '/my-security' are ALWAYS accessible (see
 * ALWAYS_ALLOWED below) and are not part of the catalogue.
 *
 * Keep this in sync with Layout.tsx navGroups when panel sections change.
 */

import { ADMIN_SECTIONS, levelFor, type RolePermissions, type AccessLevel } from '@platform/shared-types';

export { ADMIN_SECTIONS };

export interface PanelPermissionItem {
  path: string;
  label: string;
  /** Rendered indented under a parent whose path prefixes this one. */
  child?: boolean;
}

export interface PanelPermissionGroup {
  title: string;
  items: PanelPermissionItem[];
}

/** Built from ADMIN_SECTIONS, grouped by `group`, in catalogue order. */
export const PANEL_PERMISSION_GROUPS: PanelPermissionGroup[] = (() => {
  const groups: PanelPermissionGroup[] = [];
  const byTitle = new Map<string, PanelPermissionGroup>();
  for (const section of ADMIN_SECTIONS) {
    let group = byTitle.get(section.group);
    if (!group) {
      group = { title: section.group, items: [] };
      byTitle.set(section.group, group);
      groups.push(group);
    }
    group.items.push({ path: section.id, label: section.label });
  }
  return groups;
})();

/**
 * Detail routes that live under a different prefix than the permission that
 * governs them (e.g. /tenant/:id belongs to the Tenants permission).
 */
const ROUTE_ALIASES: Array<[routePrefix: string, permissionPath: string]> = [
  ['/tenant/', '/tenants-list'],
  ['/deals/', '/deals'],
  ['/affiliates/', '/affiliates'],
  ['/payments/', '/payments'],
  ['/deployments/', '/deployments'],
  ['/app-issues/', '/app-issues'],
  // Retired AI paths. Kept so a staff role saved before the merge still
  // opens the page; removing them would silently revoke access.
  ['/ai-status', '/ai'],
  ['/ai-usage', '/ai'],
  ['/business-transfers/', '/business-transfers'],
  // Controller ruling R19 (2026-10-04, Task 11 fix round 1): real pages under
  // /inventory/* that no ADMIN_SECTIONS id covers and that are NOT getting a
  // new shared-catalogue section — UI-only aliases onto the nearest existing
  // section so a role that can reach Sales/Stock isn't fail-closed-denied on
  // them. These are prefix aliases (trailing slash) because each has its own
  // sub-routes (e.g. /inventory/bundles-editor/new, /:bundleId).
  ['/inventory/bundles-editor/', '/inventory/sales'],
  ['/inventory/bundles/', '/inventory/sales'],
  ['/inventory/deals/', '/deals'],
  ['/inventory/units/', '/inventory/stock'],
];

/**
 * Redirect-only mounts whose EXACT pathname (never a prefix) aliases onto the
 * page they redirect to (src/App.tsx). These must be exact-match only: a
 * prefix alias on '/inventory' or '/legal' would wrongly swallow every real
 * sibling section ('/inventory/finance-book', '/legal/agreements', ...), and
 * a prefix alias on '/' would match literally every path.
 */
const EXACT_ROUTE_ALIASES: Record<string, string> = {
  '/': '/command-center',
  '/inventory': '/inventory/stock',
  '/inventory/catalog': '/inventory/stock',       // legacy hub mount, defaultTab="stock"
  '/inventory/procurement': '/inventory/receiving', // legacy hub mount, defaultTab="grn"
  '/inventory/models': '/inventory/stock',
  '/legal': '/legal/policies',
};

export function permissionCovers(permission: string, path: string): boolean {
  if (permission === '*') return true;
  return path === permission || path.startsWith(`${permission}/`);
}

/** Route paths every authenticated staff member may always open. */
// /my-security: every admin must always reach their own MFA + recovery codes.
const ALWAYS_ALLOWED = ['/staff-portal', '/my-security'];

export function pathAllowed(permissions: string[], pathname: string): boolean {
  if (ALWAYS_ALLOWED.some(p => permissionCovers(p, pathname))) return true;
  const candidates = [pathname];
  for (const [prefix, permissionPath] of ROUTE_ALIASES) {
    if (pathname === prefix.slice(0, -1) || pathname.startsWith(prefix)) candidates.push(permissionPath);
  }
  return permissions.some(perm => candidates.some(c => permissionCovers(perm, c)));
}

/** Longest section id that covers this pathname (aliases applied). */
export function sectionForPath(pathname: string): string | null {
  let p = EXACT_ROUTE_ALIASES[pathname] ?? pathname;
  for (const [prefix, target] of ROUTE_ALIASES) if (p === prefix.slice(0, -1) || p.startsWith(prefix)) { p = target; break; }
  const hits = ADMIN_SECTIONS.map(s => s.id).filter(id => p === id || p.startsWith(`${id}/`));
  return hits.sort((a, b) => b.length - a.length)[0] || null;
}

export function levelForPath(levels: RolePermissions, pathname: string): AccessLevel | null {
  if (ALWAYS_ALLOWED.some(a => pathname === a || pathname.startsWith(`${a}/`))) return 'edit';
  const s = sectionForPath(pathname);
  return s ? levelFor(levels, s) : null;
}
